import { Controller } from "@hotwired/stimulus"

interface VectorizePair {
  inputUrl: string
  outputUrl: string
  prompt?: string | null
}

/**
 * Vectorize Showcase Controller
 *
 * Thin wrapper around the model-page vectorize hero. The hero itself is an
 * `image-comparison` slider (raster → vector wipe); this controller owns the
 * filmstrip that swaps the active pair in place by re-pointing the slider's
 * two <img> srcs, and keeps the expand-to-lightbox button's slider URLs in
 * sync with the active pair.
 *
 * No layout math needed: image-comparison is purely container-relative, so
 * changing the image srcs is enough — the divider position is preserved.
 */
export default class extends Controller<HTMLElement> {
  static targets = ["beforeImg", "afterImg", "frame", "expand"]
  static values = { pairs: Array }

  declare readonly beforeImgTarget: HTMLImageElement
  declare readonly afterImgTarget: HTMLImageElement
  declare readonly frameTargets: HTMLElement[]
  declare readonly expandTarget: HTMLElement
  declare readonly hasBeforeImgTarget: boolean
  declare readonly hasAfterImgTarget: boolean
  declare readonly hasExpandTarget: boolean

  declare pairsValue: VectorizePair[]

  setPair(event: Event): void {
    const button = event.currentTarget as HTMLElement
    const index = Number(button.dataset.pairIndex)
    const pair = this.pairsValue[index]
    if (!pair) return

    if (this.hasBeforeImgTarget) this.beforeImgTarget.src = pair.inputUrl
    if (this.hasAfterImgTarget) this.afterImgTarget.src = pair.outputUrl

    if (this.hasExpandTarget) {
      this.expandTarget.dataset.lightboxSliderInput = pair.inputUrl
      this.expandTarget.dataset.lightboxSliderOutput = pair.outputUrl
      if (pair.prompt) {
        this.expandTarget.dataset.lightboxPrompt = pair.prompt
      } else {
        delete this.expandTarget.dataset.lightboxPrompt
      }
    }

    this.frameTargets.forEach((frame) => {
      frame.classList.toggle("is-active", frame === button)
    })
  }
}
